import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { Switch } from '@ui/switch.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiAdmintypologiesIdPatch } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import type { FC, FormEvent } from 'react';
import type { TypologyTypologyReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Typology = TypologyTypologyReadModel;

interface UpdateTypologyFormProps {
  typology: Typology;
  onSuccess: () => void;
  onConfirmSetDefault: (onConfirm: () => void) => void;
}

export const UpdateTypologyForm: FC<UpdateTypologyFormProps> = ({
  typology,
  onSuccess,
  onConfirmSetDefault
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const FormSchema = z.object({
    name: z
      .string()
      .min(3, t('form.error.char.min', { minChar: 3 }))
      .max(100, t('form.error.char.max', { maxChar: 100 }))
      .describe(t('form.schema.describe.name')),
    pointCoeff: z.coerce.number().min(0).describe(t('admin.typology.schema.describe.point.coeff')),
    isDefault: z.boolean()
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: typology.name,
      pointCoeff: typology.pointCoeff,
      isDefault: typology.isDefault
    }
  });

  const updateMutation = useApiAdmintypologiesIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.typology.update.success'));
        onSuccess();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'patch', '/admin/typologies')) {
          showOrvalErrorToast(error, t('admin.typology.update.error'));
        } else {
          toast.error(t('admin.typology.update.error'));
        }
      },
      networkMode: 'always'
    }
  });

  function onSubmit(data: FormValues) {
    const isSettingAsDefault = data.isDefault && !typology.isDefault;

    if (isSettingAsDefault) {
      onConfirmSetDefault(() =>
        updateMutation.mutate({
          id: String(typology.id),
          data: { name: data.name, pointCoeff: data.pointCoeff, isDefault: data.isDefault }
        })
      );
      return;
    }

    updateMutation.mutate({
      id: String(typology.id),
      data: { name: data.name, pointCoeff: data.pointCoeff, isDefault: data.isDefault }
    });
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit(onSubmit)(e);
  };

  const dynamicPointsName = t('point.name_other');
  const mutedText = t('admin.typology.coeff.description.text.muted', {
    pointsName: dynamicPointsName
  });

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.name.form.input.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.typology.name.form.input.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="pointCoeff"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.coeff.form.input.label')}</FormLabel>
              <ul className="flex flex-col gap-3 pl-5 text-sm list-disc text-start text-foreground">
                <li>
                  <Trans
                    i18nKey="admin.typology.coeff.description.text1"
                    components={{ strong: <strong /> }}
                    values={{ pointsName: dynamicPointsName }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey="admin.typology.coeff.description.text2"
                    components={{ strong: <strong /> }}
                    values={{ pointsName: dynamicPointsName }}
                  />
                </li>
                <span className="text-muted-foreground">{mutedText}</span>
              </ul>
              <FormControl>
                <Input
                  placeholder={t('admin.typology.coeff.form.input.placeholder')}
                  type="number"
                  min={0}
                  step={0.001}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.typology.default.form.input.label')}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={typology.isDefault}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          rounded="full"
          loading={updateMutation.isPending}
          className="w-full"
          type="submit"
          disabled={!form.formState.isDirty || updateMutation.isPending}
        >
          {t('admin.typology.update.form.button')}
        </Button>
      </form>
    </Form>
  );
};
